import React, { useState, useEffect } from 'react';
import { User, Category, ThemeMode, UserTier, UserRole, SystemConfig, NotificationSettings, SystemEvent, FeedbackType } from '../types';
import { firebaseService, getAuthHeaders } from '../services/firebaseService';
import { API_BASE } from '../apiConfig';
import { notificationService } from '../services/notificationService';
import { Link } from 'react-router-dom';
import { 
  Settings, Moon, Sun, Calendar, Plus, Trash2, CheckCircle, 
  AlertTriangle, Save, Loader2, Palette, Globe, Shield, RefreshCw, Lock, Layers,
  UserX, CreditCard, ExternalLink, ShieldCheck, Zap, Info, Key, Eye, EyeOff,
  Bell, BellOff, Clock, Smartphone, MessageSquare, Target
} from 'lucide-react';
import { useActionFlow } from './ActionModal';

interface Props {
  user: User | null;
  onUserUpdate: (user: User) => void;
  onDeleteAccount: () => void;
  categories: Category[];
  onCategoriesUpdate: (cats: Category[]) => void;
}

const EVENT_OPTIONS: { id: SystemEvent; label: string; desc: string }[] = [
  { id: 'nudge_urgent', label: 'Crisis Stagnation (Q1)', desc: 'Alert when Q1 tasks exceed 24h' },
  { id: 'nudge_strategic', label: 'Strategic Drift (Q2)', desc: 'Reminder for the Strategic Moat' },
  { id: 'completed', label: 'Objective Secured', desc: 'Confirmations of completed missions' },
  { id: 'partner_request', label: 'Partner Status Requests', desc: 'Alerts when partners request updates' },
  { id: 'partner_update', label: 'Partner Intelligence', desc: 'Updates from accountability partners' },
  { id: 'daily', label: 'Daily Momentum', desc: 'Daily execution pulse with Coach Insight' },
  { id: 'inactivity_7_days' as SystemEvent, label: 'Inactivity Reminder', desc: 'Nudge after 7 days of no activity' },
  { id: 'streak_7_day' as SystemEvent, label: 'Streak Celebration', desc: 'Reward for 7-day execution streaks' }
];

export const SettingsPage: React.FC<Props> = ({ user, onUserUpdate, onDeleteAccount, categories, onCategoriesUpdate }) => {
  const { startAction, openAlert } = useActionFlow();
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#6366f1');
  const [billingLoading, setBillingLoading] = useState(false);
  
  // Notification State - Explicitly set pushEnabled to true by default for all users
  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(() => {
    const existing = user?.settings?.notifications;
    return {
      pushEnabled: existing ? (existing.pushEnabled !== false) : true, 
      quietHoursEnabled: existing?.quietHoursEnabled ?? true,
      quietHoursStart: existing?.quietHoursStart || "22:00",
      quietHoursEnd: existing?.quietHoursEnd || "08:00",
      notifyOn: existing?.notifyOn || ['nudge_urgent', 'nudge_strategic', 'completed', 'partner_request', 'partner_update', 'daily', 'inactivity_7_days', 'streak_7_day']
    };
  });

  const isAdmin = user?.role === UserRole.ADMIN;
  const isPremium = user?.tier === UserTier.PREMIUM || isAdmin;

  const handleThemeToggle = async (mode: ThemeMode) => {
    if (!user) return;
    const updatedUser = { 
      ...user, 
      settings: { ...user.settings, theme: mode } as any 
    };
    onUserUpdate(updatedUser);
    await firebaseService.updateUser(user.id, { settings: updatedUser.settings });
  };

  const handleSaveNotifications = async () => {
    if (!user) return;
    
    startAction({
      title: 'Sync Shards',
      message: 'Update tactical alert registry configurations?',
      confirmLabel: 'Sync',
      variant: 'info',
      action: async () => {
        if (notifSettings.pushEnabled && Notification.permission !== 'granted') {
          const granted = await notificationService.requestPermission();
          if (!granted) {
            setNotifSettings(prev => ({ ...prev, pushEnabled: false }));
            throw new Error("Permission Denied. Browser alerts cannot be enabled.");
          }
        }

        const updatedUser = {
          ...user,
          settings: {
            ...user.settings,
            notifications: notifSettings
          } as any
        };
        
        await firebaseService.updateUser(user.id, { settings: updatedUser.settings });
        onUserUpdate(updatedUser);
        notificationService.sendLocal("System Updated", "Tactical alert registry synchronized.");
      },
      successMessage: 'Alert settings synchronized.'
    });
  };

  const toggleEvent = (id: SystemEvent) => {
    setNotifSettings(prev => ({
      ...prev,
      notifyOn: prev.notifyOn.includes(id) 
        ? prev.notifyOn.filter(e => e !== id)
        : [...prev.notifyOn, id]
    }));
  };

  const handleCreateCategory = async () => {
    if (!newCatName.trim() || !user) return;
    const newCat: Category = {
      id: Math.random().toString(36).substr(2, 9),
      userId: user.id,
      name: newCatName,
      color: newCatColor
    };
    // No confirmation needed for non-destructive creation, but we could wrap if consistent feedback desired
    try {
      const created = await firebaseService.createCategory(newCat);
      onCategoriesUpdate([...categories, created]);
      setNewCatName('');
    } catch (e) {
      openAlert({ title: "Error", message: "Failed to create category.", variant: "danger" });
    }
  };

  const handleDeleteCategory = async (id: string) => {
    const cat = categories.find(c => c.id === id);
    if (!cat || cat.isDefault) {
      if (cat?.isDefault) openAlert({ title: "Restricted", message: "Primary category cannot be removed from global registry.", variant: "warning" });
      return;
    }

    startAction({
      title: 'Remove Category',
      message: `Remove "${cat.name.toUpperCase()}"? All linked objectives will be reassigned to Primary.`,
      confirmLabel: 'Remove',
      variant: 'danger',
      action: async () => {
        const updatedCats = categories.filter(c => c.id !== id);
        onCategoriesUpdate(updatedCats); // Optimistic update
        try {
          await firebaseService.deleteCategory(id);
        } catch (e) {
          // Revert optimistic update
          const originalCats = await firebaseService.getCategories(user?.id || 'system');
          onCategoriesUpdate(originalCats);
          throw e;
        }
      },
      successMessage: 'Category removed.'
    });
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const isPremium = user.tier === 'PREMIUM';
      if (isPremium && user.stripeId) {
        // Redirect to Stripe Customer Portal
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE}/api/stripe/create-portal-session`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ stripeId: user.stripeId })
        });
        const data = await res.json();
        if (data.url) { window.location.href = data.url; return; }
        throw new Error(data.error || 'Failed to create portal session');
      } else {
        // Redirect to Stripe Checkout
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ userId: user.id, userEmail: user.email, plan: 'yearly' })
        });
        const data = await res.json();
        if (data.url) { window.location.href = data.url; return; }
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (err: any) {
      console.error('[Settings] Billing error:', err);
      openAlert({ title: "Billing Error", message: err.message || "Could not connect to payment provider. Please try again.", variant: 'error' });
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-12 pb-20 animate-in fade-in duration-500">
      <div className="flex justify-between items-end border-b border-vanguard-border pb-8">
        <div className="space-y-1">
          <h2 className="text-4xl font-black text-vanguard-text tracking-tighter uppercase leading-none">Settings</h2>
          <p className="text-[10px] font-mono text-vanguard-muted uppercase tracking-[0.3em] font-bold">Configure Tactical Preferences</p>
        </div>
        <Settings className="text-blue-600 dark:text-indigo-400 animate-spin-slow" size={32} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Appearance */}
        <div className="glass p-8 rounded-[2rem] space-y-6 shadow-sm border border-vanguard-border">
          <div className="flex items-center gap-3 mb-2">
            <Palette className="text-indigo-500 dark:text-indigo-400" size={20} />
            <h3 className="text-xl font-black text-vanguard-text uppercase tracking-tighter">Appearance</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button 
              onClick={() => handleThemeToggle('dark')}
              className={`flex flex-col items-center gap-4 p-6 rounded-2xl border-2 transition-all ${user?.settings?.theme === 'dark' ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-vanguard-bg border-vanguard-border text-vanguard-muted hover:border-vanguard-muted'}`}
            >
              <Moon size={32} />
              <span className="text-[10px] font-black uppercase tracking-widest">Dark Mode</span>
            </button>
            <button 
              onClick={() => handleThemeToggle('light')}
              className={`flex flex-col items-center gap-4 p-6 rounded-2xl border-2 transition-all ${user?.settings?.theme === 'light' ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-vanguard-bg border-vanguard-border text-vanguard-muted hover:border-vanguard-muted'}`}
            >
              <Sun size={32} />
              <span className="text-[10px] font-black uppercase tracking-widest">Light Mode</span>
            </button>
          </div>
        </div>

        {/* Tactical Billing Integration */}
        <div className="glass p-8 rounded-[2rem] space-y-6 relative overflow-hidden shadow-sm border border-vanguard-border bg-gradient-to-br from-indigo-500/5 to-transparent">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="text-blue-600" size={20} />
            <h3 className="text-xl font-black text-vanguard-text uppercase tracking-tighter">Tactical Billing</h3>
          </div>
          <div className="p-6 bg-vanguard-bg rounded-2xl border border-vanguard-border space-y-4 shadow-inner">
             <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-vanguard-text uppercase tracking-widest">Service Status</span>
                <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${isPremium ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600' : 'bg-slate-200 border-slate-300 text-slate-500'}`}>
                  {isPremium ? 'Premium Active' : 'Basic Tier'}
                </div>
             </div>
             
             {isPremium ? (
               <div className="space-y-3">
                 <p className="text-[10px] text-vanguard-muted font-bold leading-relaxed">Your subscription is managed via Stripe Secure Relay. Modify plans or update payment methods below.</p>
                 <button 
                    onClick={handleManageBilling}
                    disabled={billingLoading}
                    className="w-full py-4 bg-vanguard-surface border border-vanguard-border hover:border-blue-500 text-vanguard-text font-black text-[10px] rounded-xl transition-all uppercase tracking-widest flex items-center justify-center gap-3 shadow-sm active:scale-95"
                 >
                   {billingLoading ? <Loader2 className="animate-spin" size={14}/> : <ExternalLink size={14}/>}
                   Manage Subscription
                 </button>
               </div>
             ) : (
               <div className="space-y-3">
                 <p className="text-[10px] text-vanguard-muted font-bold leading-relaxed">Upgrade to Vanguard Premium for AI subtask decomposition, voice-to-task, and advanced tactical analytics.</p>
                 <button 
                    onClick={handleManageBilling}
                    disabled={billingLoading}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-black text-[10px] rounded-xl transition-all uppercase tracking-widest flex items-center justify-center gap-3 shadow-lg shadow-blue-600/20 active:scale-95"
                 >
                   <Zap size={14} fill="currentColor" /> Upgrade to Premium
                 </button>
               </div>
             )}
          </div>
        </div>
      </div>

      {/* Tactical Alerts Control Panel */}
      <div className="glass p-10 rounded-[3rem] space-y-10 shadow-sm border border-vanguard-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-rose-500/10 rounded-2xl text-rose-500">
              <Bell size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-vanguard-text uppercase tracking-tighter leading-none">Tactical Alerts</h3>
              <p className="text-[9px] font-mono text-vanguard-muted uppercase tracking-[0.3em] mt-1.5">Mobile Push & Dispatch Registry</p>
            </div>
          </div>
          <button 
            onClick={handleSaveNotifications}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 flex items-center gap-2"
          >
            <Save size={14}/> Sync Shards
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Master Toggles */}
          <div className="space-y-8">
            <div className="p-6 bg-vanguard-bg border border-vanguard-border rounded-[2rem] space-y-6 shadow-inner">
               <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Smartphone className="text-blue-500" size={20} />
                    <span className="text-xs font-black text-vanguard-text uppercase tracking-widest">Mobile Push Alerts</span>
                  </div>
                  <button 
                    onClick={() => setNotifSettings({...notifSettings, pushEnabled: !notifSettings.pushEnabled})}
                    className={`w-14 h-7 rounded-full transition-all relative ${notifSettings.pushEnabled ? 'bg-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-slate-200 dark:bg-slate-800'}`}
                  >
                    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${notifSettings.pushEnabled ? 'right-1' : 'left-1'}`} />
                  </button>
               </div>
               <p className="text-[10px] text-vanguard-muted font-bold leading-relaxed px-1">
                 Authorize direct Lock Screen telemetry. This shard allows the system to reach you even when the tactical app is closed.
               </p>
            </div>

            <div className="p-6 bg-vanguard-bg border border-vanguard-border rounded-[2rem] space-y-6 shadow-inner">
               <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Clock className="text-amber-500" size={20} />
                    <span className="text-xs font-black text-vanguard-text uppercase tracking-widest">Operation Quiet Hours</span>
                  </div>
                  <button 
                    onClick={() => setNotifSettings({...notifSettings, quietHoursEnabled: !notifSettings.quietHoursEnabled})}
                    className={`w-14 h-7 rounded-full transition-all relative ${notifSettings.quietHoursEnabled ? 'bg-blue-600 shadow-lg shadow-blue-600/20' : 'bg-slate-200 dark:bg-slate-800'}`}
                  >
                    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${notifSettings.quietHoursEnabled ? 'right-1' : 'left-1'}`} />
                  </button>
               </div>
               {notifSettings.quietHoursEnabled && (
                 <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2">
                    <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-vanguard-muted uppercase tracking-widest ml-1">Cease Alerting</label>
                       <input 
                        type="time" 
                        value={notifSettings.quietHoursStart} 
                        onChange={e => setNotifSettings({...notifSettings, quietHoursStart: e.target.value})}
                        className="w-full bg-vanguard-surface border border-vanguard-border rounded-xl px-4 py-2.5 text-xs font-bold outline-none focus:border-blue-500"
                       />
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-vanguard-muted uppercase tracking-widest ml-1">Resume Relay</label>
                       <input 
                        type="time" 
                        value={notifSettings.quietHoursEnd} 
                        onChange={e => setNotifSettings({...notifSettings, quietHoursEnd: e.target.value})}
                        className="w-full bg-vanguard-surface border border-vanguard-border rounded-xl px-4 py-2.5 text-xs font-bold outline-none focus:border-blue-500"
                       />
                    </div>
                 </div>
               )}
               <p className="text-[10px] text-vanguard-muted font-bold leading-relaxed px-1">
                 Mute all non-critical telemetry during recovery phases. Prevents midnight strategic drift alerts.
               </p>
            </div>
          </div>

          {/* Event Specifics */}
          <div className="space-y-4">
             <label className="text-[10px] font-black text-vanguard-muted uppercase tracking-[0.2em] ml-2">Alert Triggers</label>
             <div className="space-y-3">
                {EVENT_OPTIONS.map(opt => (
                  <button 
                    key={opt.id}
                    onClick={() => toggleEvent(opt.id)}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all flex items-center justify-between group ${notifSettings.notifyOn.includes(opt.id) ? 'bg-vanguard-bg border-blue-600/30' : 'bg-vanguard-surface border-transparent opacity-60'}`}
                  >
                    <div className="flex items-center gap-4">
                       <div className={`p-3 rounded-xl ${notifSettings.notifyOn.includes(opt.id) ? 'bg-blue-600 text-white' : 'bg-black/5 text-vanguard-muted'}`}>
                          {opt.id.includes('nudge') ? <Target size={16}/> : opt.id.includes('partner') ? <Smartphone size={16}/> : <CheckCircle size={16}/>}
                       </div>
                       <div>
                          <p className={`text-xs font-black uppercase tracking-tight ${notifSettings.notifyOn.includes(opt.id) ? 'text-vanguard-text' : 'text-vanguard-muted'}`}>{opt.label}</p>
                          <p className="text-[9px] font-bold text-vanguard-muted opacity-80 mt-0.5">{opt.desc}</p>
                       </div>
                    </div>
                    {notifSettings.notifyOn.includes(opt.id) && <CheckCircle className="text-blue-600" size={16}/>}
                  </button>
                ))}
             </div>
          </div>
        </div>
      </div>

      <div className="glass p-10 rounded-[3rem] space-y-10 shadow-sm border border-vanguard-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/10 rounded-2xl text-amber-500">
              <Layers size={24} />
            </div>
            <h3 className="text-2xl font-black text-vanguard-text uppercase tracking-tighter">Categories</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1 space-y-6">
             <div className="space-y-3">
                <label className="text-[9px] font-mono text-vanguard-muted uppercase font-black tracking-[0.3em]">New Category</label>
                <input 
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  placeholder="IDENTIFIER..."
                  className="w-full bg-vanguard-bg border border-vanguard-border rounded-2xl px-6 py-5 text-xs text-vanguard-text font-black outline-none focus:ring-4 focus:ring-blue-600/5 transition-all shadow-inner uppercase"
                />
             </div>
             <div className="space-y-3">
                <label className="text-[9px] font-mono text-vanguard-muted uppercase font-black tracking-[0.3em]">Visual Color</label>
                <div className="flex items-center gap-4">
                  <input 
                    type="color"
                    value={newCatColor}
                    onChange={(e) => setNewCatColor(e.target.value)}
                    className="w-16 h-16 bg-vanguard-bg border border-vanguard-border rounded-2xl p-2 cursor-pointer shadow-sm"
                  />
                  <div className="flex-1 p-5 rounded-2xl bg-vanguard-bg border border-vanguard-border text-[10px] font-mono text-vanguard-muted font-bold shadow-inner">{newCatColor.toUpperCase()}</div>
                </div>
             </div>
             <button 
               onClick={handleCreateCategory}
               className="w-full py-5 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[11px] rounded-2xl transition-all uppercase tracking-[0.2em] flex items-center justify-center gap-3 shadow-xl shadow-emerald-500/10 active:scale-95"
             >
               <Plus size={18}/> Save Category
             </button>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto custom-scrollbar pr-4">
            {categories.sort((a,b) => (a.isDefault ? -1 : 1)).map(cat => (
              <div 
                key={cat.id} 
                className="relative flex items-center justify-between p-7 rounded-[1.75rem] bg-vanguard-bg border border-vanguard-border group hover:border-blue-500/30 transition-all shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="w-4 h-4 rounded-full shadow-lg shrink-0" style={{ backgroundColor: cat.color }}></div>
                  <div className="flex flex-col">
                    <span className="text-xs font-black text-vanguard-text uppercase tracking-tight">{cat.name}</span>
                    {cat.isDefault && (
                      <span className="text-[8px] font-mono text-blue-600 dark:text-blue-400 font-bold tracking-widest uppercase">Default</span>
                    )}
                  </div>
                </div>
                
                {!cat.isDefault && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }} 
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-vanguard-surface border border-vanguard-border text-vanguard-muted hover:text-rose-500 hover:bg-rose-500/5 transition-all opacity-40 group-hover:opacity-100"
                    title="Delete Category"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Send Feedback */}
      <div className="glass p-10 rounded-[3rem] space-y-6 shadow-sm border border-vanguard-border">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-500/10 rounded-2xl text-blue-600">
            <MessageSquare size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-black text-vanguard-text uppercase tracking-tighter">Send Feedback</h3>
            <p className="text-[10px] text-vanguard-muted font-bold uppercase tracking-widest mt-0.5">Report bugs, share ideas, or flag concerns</p>
          </div>
        </div>
        {(() => {
          const [feedbackType, setFeedbackType] = useState<FeedbackType>('BUG');
          const [feedbackMsg, setFeedbackMsg] = useState('');
          const [feedbackSent, setFeedbackSent] = useState(false);
          const [feedbackSending, setFeedbackSending] = useState(false);

          const typeOptions: { id: FeedbackType; label: string; icon: React.ReactNode }[] = [
            { id: 'BUG', label: 'Bug / Issue', icon: <AlertTriangle size={14} /> },
            { id: 'PERFORMANCE', label: 'Performance', icon: <Zap size={14} /> },
            { id: 'FEATURE', label: 'Feature Idea', icon: <Target size={14} /> },
          ];

          if (feedbackSent) {
            return (
              <div className="flex flex-col items-center gap-3 py-8">
                <CheckCircle size={32} className="text-emerald-500" />
                <p className="text-sm font-bold text-emerald-600">Feedback received. Thank you!</p>
                <button onClick={() => { setFeedbackSent(false); setFeedbackMsg(''); }} className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:text-slate-600">
                  Send another
                </button>
              </div>
            );
          }

          return (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {typeOptions.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFeedbackType(t.id)}
                    className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                      feedbackType === t.id
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-400 hover:bg-slate-200 border border-slate-200'
                    }`}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
              <textarea
                value={feedbackMsg}
                onChange={(e) => setFeedbackMsg(e.target.value)}
                placeholder={
                  feedbackType === 'BUG' ? 'Describe the issue you encountered...' :
                  feedbackType === 'PERFORMANCE' ? 'What feels slow or unresponsive?' :
                  'Describe the feature or improvement you\'d like to see...'
                }
                rows={3}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 resize-none"
              />
              <button
                disabled={!feedbackMsg.trim() || feedbackSending}
                onClick={async () => {
                  if (!user || !feedbackMsg.trim()) return;
                  setFeedbackSending(true);
                  try {
                    await firebaseService.submitFeedback({
                      userId: user.id,
                      userEmail: user.email,
                      userName: user.displayName || user.email,
                      type: feedbackType,
                      message: feedbackMsg.trim(),
                      status: 'NEW',
                      createdAt: Date.now(),
                      updatedAt: Date.now(),
                    });
                    setFeedbackSent(true);
                  } catch (e) {
                    console.error('Feedback submit failed:', e);
                  }
                  setFeedbackSending(false);
                }}
                className="w-full py-3.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600 text-white font-black text-[10px] rounded-xl transition-all uppercase tracking-[0.2em] flex items-center justify-center gap-2"
              >
                {feedbackSending ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                Submit Feedback
              </button>
            </div>
          );
        })()}
      </div>

      <div className="glass p-10 rounded-[3rem] space-y-6 shadow-sm border border-rose-500/20 bg-rose-500/5">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-rose-500/10 rounded-2xl text-rose-500">
            <AlertTriangle size={24} />
          </div>
          <h3 className="text-2xl font-black text-rose-600 dark:text-rose-400 uppercase tracking-tighter">Danger Zone</h3>
        </div>
        <p className="text-sm text-vanguard-muted font-medium leading-relaxed max-w-2xl">
          Deleting your account will scrub your tactical profile, all mission objectives, and historical logs from the Vanguard network. This action cannot be undone.
        </p>
        <button 
          id="scrub-tactical-profile-button"
          onClick={() => {
            startAction({
              title: 'Scrub Profile',
              message: 'FINAL WARNING: Initiate complete profile purge? All data will be permanently scrubbed from the Vanguard Grid.',
              confirmLabel: 'Permanently Delete',
              variant: 'danger',
              action: async () => {
                onDeleteAccount();
              }
            });
          }}
          className="px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white font-black text-[10px] rounded-xl transition-all uppercase tracking-[0.2em] flex items-center gap-3 shadow-lg shadow-rose-600/20 active:scale-95"
        >
          <UserX size={18}/> Scrub Tactical Profile
        </button>
      </div>
    </div>
  );
};